import { FetchBaseQueryError } from "@reduxjs/toolkit/query";

/**
 * Type predicate to narrow an unknown error to `FetchBaseQueryError`
 */
export function isFetchBaseQueryError(
  error: unknown
): error is FetchBaseQueryError {
  return typeof error === "object" && error != null && "status" in error;
}

/**
 * Type predicate to narrow an unknown error to an object with a string 'message' property
 */
export function isErrorWithMessage(error: unknown): error is {
  data: {
    message: string;
  };
} {
  return (
    typeof error === "object" &&
    error != null &&
    "data" in error &&
    "message" in (error as any).data &&
    typeof (error as any).data.message === "string"
  );
}

/**
 * For file downloads, using `response.blob()` for every status turns JSON error bodies
 * (e.g. Lynx ErrorDto) into Blobs so RTK Query cannot surface `data.message`.
 * Use this handler instead: OK responses become Blobs; error responses become parsed JSON.
 */
export async function blobDownloadResponseHandler(
  response: Response
): Promise<Blob | Record<string, unknown>> {
  if (!response.ok) {
    const text = await response.text();
    if (!text?.trim()) {
      return { message: response.statusText || "Request failed" };
    }
    try {
      return JSON.parse(text) as Record<string, unknown>;
    } catch {
      return { message: text };
    }
  }
  return response.blob();
}

/**
 * User-visible message from an RTK Query rejected payload (`FetchBaseQueryError`).
 */
export function getRtkQueryRejectedMessage(
  payload: unknown
): string | undefined {
  if (typeof payload !== "object" || payload === null || !("data" in payload)) {
    return undefined;
  }
  const { data } = payload as { data: unknown };
  if (typeof data === "string" && data.trim()) {
    return data;
  }
  if (typeof data === "object" && data !== null) {
    const record = data as Record<string, unknown>;
    const msg = record.message ?? record.Message;
    if (typeof msg === "string" && msg.trim()) {
      return msg;
    }
  }
  return undefined;
}
